import {
  bfsMovementRange,
  canMoveAndAttackSameTurn,
  coordEq,
  coordKey,
  effectiveAttackKind,
  forecastCounterDamage,
  isIndirectFire,
  unitAt,
  unitTotalHp,
} from "@dailywars/core/engine";
import type { Coord, GameState, Unit } from "@dailywars/core/engine";

import type { AttackRangeFlashIconStyle } from "#/game/attackRangeFlashIcon";
import { getBoardActionHint, type BoardActionHint } from "#/game/selection/actionHint";
import { getBoardCursorMode } from "#/game/selection/cursor";
import { computeAttackTileKeysFromPos } from "#/game/selection/options";
import { isOwnSelectableUnit } from "#/game/selection/transitions";
import type { BoardCursorMode, PendingAttack, SelectionState } from "#/game/selection/types";
import type { StoreState } from "#/game/store/types";

export function reachableKeysFromSelection(selection: SelectionState, state: GameState): string[] {
  if (
    selection.mode === "none" ||
    selection.mode === "confirm-attack" ||
    selection.mode === "building" ||
    selection.mode === "enemy-inspect" ||
    selection.mode === "transport-unload"
  ) {
    if (selection.mode === "transport-unload") {
      return selection.unloadTiles.map((coord) => coordKey(coord));
    }
    return [];
  }
  const unit = state.units.find((u) => u.id === selection.unitId);
  const keys = new Set(
    unit
      ? bfsMovementRange(state, unit).map((coord) => coordKey(coord))
      : selection.reachable.map((coord) => coordKey(coord)),
  );
  for (const join of selection.joinOptions) {
    const target = state.units.find((u) => u.id === join.targetId);
    if (target) keys.add(coordKey(target.pos));
  }
  for (const load of selection.loadOptions) {
    const target = state.units.find((u) => u.id === load.targetId);
    if (target) keys.add(coordKey(target.pos));
  }
  return [...keys];
}

export function attackableTileKeysFromSelection(selection: SelectionState): string[] {
  if (selection.mode === "enemy-inspect") {
    return selection.attackableTileKeys;
  }
  return [];
}

export function attackRangeFlashTileKeysFromSelection({
  selection,
  state,
}: {
  selection: SelectionState;
  state: GameState;
}): string[] {
  if (selection.mode !== "unit" && selection.mode !== "move-preview") return [];
  const unit = state.units.find((candidate) => candidate.id === selection.unitId);
  if (!unit || unit.hasAttacked) return [];
  const attackKind = effectiveAttackKind(unit);
  if (!isIndirectFire(attackKind)) return [];
  const fromPos = selection.mode === "move-preview" ? selection.destination : unit.pos;
  return computeAttackTileKeysFromPos({
    state,
    kind: attackKind,
    pos: fromPos,
  });
}

export function attackRangeFlashStyleFromSelection({
  selection,
  state,
}: {
  selection: SelectionState;
  state: GameState;
}): AttackRangeFlashIconStyle {
  if (selection.mode !== "unit" && selection.mode !== "move-preview") return "strike";
  const unit = state.units.find((candidate) => candidate.id === selection.unitId);
  if (!unit) return "strike";
  if (
    !canMoveAndAttackSameTurn(unit.kind) &&
    (selection.mode === "move-preview" || unit.hasMoved)
  ) {
    return "coverage";
  }
  return "strike";
}

export function pathKeysFromSelection(selection: SelectionState): string[] {
  if (selection.mode === "move-preview") {
    return selection.path.map((coord) => coordKey(coord));
  }
  if (selection.mode === "confirm-attack") {
    return selection.path.map((coord) => coordKey(coord));
  }
  return [];
}

export function attackableUnitIdsFromSelection(selection: SelectionState): string[] {
  if (selection.mode === "unit") {
    return selection.attackOptions.map((o) => o.targetId);
  }
  if (selection.mode === "move-preview") {
    return selection.attackOptions.map((o) => o.targetId);
  }
  if (selection.mode === "confirm-attack") {
    return [selection.targetId];
  }
  return [];
}

export function selectedUnitIdFromSelection(selection: SelectionState): string | null {
  if (
    selection.mode === "none" ||
    selection.mode === "building" ||
    selection.mode === "enemy-inspect"
  )
    return null;
  return selection.unitId;
}

export function pendingAttackFromSelection({
  selection,
  state,
}: {
  selection: SelectionState;
  state: GameState;
}): PendingAttack | null {
  if (selection.mode !== "confirm-attack") return null;
  const { units } = state;
  const attacker = units.find((u) => u.id === selection.unitId);
  const target = units.find((u) => u.id === selection.targetId);
  if (!attacker || !target) return null;

  const attackerDest =
    selection.path.length > 0 ? (selection.path[selection.path.length - 1] as Coord) : attacker.pos;
  const targetHp = unitTotalHp(target);
  const attackerHp = unitTotalHp(attacker);
  const counterForecast = forecastCounterDamage({
    state,
    attackerId: attacker.id,
    targetId: target.id,
    attackerDest,
    attackDamage: selection.forecast,
  });

  return {
    attackerId: selection.unitId,
    targetId: selection.targetId,
    targetPos: target.pos,
    forecast: selection.forecast,
    attackerKind: attacker.kind,
    attackerOwner: attacker.owner,
    targetKind: selection.targetKind,
    targetOwner: target.owner,
    willKillTarget: selection.forecast >= targetHp && targetHp > 0,
    counterForecast,
    willKillAttacker: counterForecast !== null && counterForecast >= attackerHp && attackerHp > 0,
  };
}

export function previewUnitFromSelection(
  selection: SelectionState,
  units: Unit[],
): { unitId: string; pos: Coord; targetPos?: Coord } | null {
  if (selection.mode === "move-preview") {
    return { unitId: selection.unitId, pos: selection.destination };
  }
  if (selection.mode === "confirm-attack") {
    const target = units.find((u) => u.id === selection.targetId);
    if (!target) return null;
    const dest =
      selection.path.length > 0
        ? (selection.path[selection.path.length - 1] as Coord)
        : (units.find((u) => u.id === selection.unitId)?.pos ?? null);
    if (!dest) return null;
    return { unitId: selection.unitId, pos: dest, targetPos: target.pos };
  }
  return null;
}

export function inspectedUnitFromState(state: StoreState): Unit | null {
  const { hoveredTile, selection, localState } = state;
  if (!hoveredTile) return null;
  if (selection.mode === "move-preview" && coordEq(selection.destination, hoveredTile)) {
    return localState.units.find((u) => u.id === selection.unitId) ?? null;
  }
  return unitAt(localState, hoveredTile) ?? null;
}

export function boardCursorFromState(state: StoreState, coarsePointer = false): BoardCursorMode {
  return getBoardCursorMode({
    state: state.localState,
    selection: state.selection,
    hoveredTile: state.hoveredTile,
    coarsePointer,
  });
}

export function boardActionHintFromState(
  state: StoreState,
  coarsePointer = false,
): BoardActionHint {
  if (state.localState.currentPlayer !== "p1" || state.localState.status !== "in_progress") {
    return { kind: "none" };
  }
  return getBoardActionHint({
    state: state.localState,
    selection: state.selection,
    hoveredTile: state.hoveredTile,
    coarsePointer,
  });
}

export function canEndTurnFromState(state: StoreState): boolean {
  return (
    !state.animation &&
    !state.isSubmitting &&
    !state.aiPlayback &&
    state.localState.currentPlayer === "p1" &&
    state.localState.status === "in_progress" &&
    state.selection.mode !== "confirm-attack"
  );
}

export function hasUnactedOwnUnitsFromState(state: StoreState): boolean {
  if (state.localState.currentPlayer !== "p1") return false;
  return state.localState.units.some((unit) => isOwnSelectableUnit(state.localState, unit));
}

export function unactedOwnUnitIdsFromState(state: StoreState): string[] {
  if (state.localState.currentPlayer !== "p1") return [];
  return state.localState.units
    .filter((unit) => isOwnSelectableUnit(state.localState, unit))
    .map((unit) => unit.id);
}

export function canUndoFromState(state: StoreState): boolean {
  return (
    !state.animation &&
    !state.isSubmitting &&
    !state.aiPlayback &&
    state.localState.currentPlayer === "p1" &&
    state.localState.status === "in_progress" &&
    state.pendingMoves.length > 0
  );
}
